import { useState } from 'react';
import { DocumentScannerOutlined } from '@mui/icons-material';
import { Box, FormControlLabel, Link, Skeleton, Switch, Typography } from '@mui/material';
import { useGo, useResourceParams, useUpdate } from '@refinedev/core';
import { Edit } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller, FieldValues, FormProvider, SubmitHandler } from 'react-hook-form';

import { Dropzone } from '@attackiq/components/Dropzone';
import { TextFieldInput } from '@attackiq/components/inputs/TextFieldInput';
import { ATTACKIQ_ID } from '@attackiq/constants';
import { fileToBase64 } from '@attackiq/utils/fileToBase64';

import { ShowInAppFormGroup } from '../../components';
import { MarkdownEditor } from '../../components/inputs';
import useCurrentResource from '../../hooks/useCurrentResource';
import { New } from '../../types';

import { ActiveInput, FeaturedInput, ModulesInput, ScenarioTagIDSInput, TypeInput } from './create';

const NewEdit = () => {
  const { query } = useCurrentResource<New>();
  const isLoading = query.isLoading;
  const [urlEnabled, setUrlEnabled] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | undefined | null>(null);
  const {
    mutateAsync: editMutateAsync,
    mutation: { isPending: isUpdating }
  } = useUpdate();
  const go = useGo();

  const { id } = useResourceParams();
  const { saveButtonProps, ...formMethods } = useForm<New>({
    refineCoreProps: {
      action: 'edit'
    },
    defaultValues: {
      content_id: '',
      content_type_name: '',
      description: '',
      url: '',
      company: ATTACKIQ_ID,
      featured: false,
      cover_image: '',
      date: '',
      id: '',
      modules: [],
      scenario_tag_ids: [],
      title: '',
      active: false,
      type: 'whats_new',
      order: 0,
      show_in_enterprise: false,
      show_in_ready: false,
      show_in_flex: false
    }
  });

  const { handleSubmit, setValue, watch } = formMethods;
  const selectedType = watch('type');

  const onSubmit: SubmitHandler<New> = async newContent => {
    if (!id) throw new Error('No ID provided');

    const payload = {
      ...newContent,
      id
    };

    if (selectedFile && !urlEnabled) payload.cover_image = await fileToBase64(selectedFile);

    const { data: editedNew } = await editMutateAsync({
      id,
      resource: 'whats_new',
      values: payload
    });

    if (!editedNew.id) return;

    go({
      to: {
        resource: 'whats_new',
        action: 'show',
        id: editedNew.id
      }
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form name="edit-module-form" onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)} autoComplete="off">
        <Edit isLoading={isUpdating} recordItemId={id} saveButtonProps={{ type: 'submit' }} canDelete={false}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={500} width="100%" />
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <ActiveInput />
              <TypeInput />
              <TextFieldInput color="primary" name="title" label="Title" rules={{ required: 'Title is required' }} />
              {selectedType === 'whats_new' && (
                <>
                  <TextFieldInput
                    color="primary"
                    name="content_id"
                    label="Content ID"
                    rules={{ required: 'Content ID is required' }}
                  />
                  <TextFieldInput
                    color="primary"
                    name="content_type_name"
                    label="Content Type Name"
                    rules={{ required: 'Content Type Name is required' }}
                  />
                  <TextFieldInput color="primary" name="url" label="URL" rules={{ required: 'URL is required' }} />
                </>
              )}
              <TextFieldInput
                color="primary"
                name="order"
                label="Order"
                type="number"
                rules={{ required: 'Order is required' }}
              />
              <Controller
                name="description"
                control={formMethods.control}
                rules={{ required: 'Description is required' }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: error ? 'error.main' : 'text.primary' }}>
                      Description {error && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                    <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />
                    {error && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
              <ModulesInput />
              {selectedType === 'whats_new' && <ScenarioTagIDSInput />}
              <FeaturedInput />
              {selectedType === 'whats_new' && (
                <>
                  <FormControlLabel
                    label="Use URL to upload cover image"
                    control={
                      <Switch
                        value={urlEnabled}
                        onChange={e => {
                          if (!e.target.checked) setValue('cover_image', '');
                          setUrlEnabled(e.target.checked);
                        }}
                      />
                    }
                  />
                  {urlEnabled ? (
                    <TextFieldInput
                      color="primary"
                      name="cover_image"
                      label="Cover Image URL"
                      rules={{
                        required: 'Cover Image is required',
                        pattern: { value: /https?:\/\/.*/, message: 'Invalid URL' }
                      }}
                    />
                  ) : (
                    <Dropzone
                      rules={{
                        accept: '.png, .jpg, .jpeg, .svg, .webp'
                      }}
                      onDropAccepted={files => {
                        setSelectedFile(files[0]);
                      }}
                      width="100%"
                      height={152}
                    >
                      <Dropzone.Body>
                        <>
                          <Typography>
                            <Link component="span">Click to upload</Link> or drag and drop
                          </Typography>
                          <Typography color="text.secondary">
                            .png, .jpg, .jpeg, .svg, .webp files supported only (max. 3MB)
                          </Typography>
                        </>
                      </Dropzone.Body>

                      <Dropzone.Cover
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        bgcolor="background.paper"
                        in={!!selectedFile}
                      >
                        <DocumentScannerOutlined color="primary" />
                        <Typography>{selectedFile?.name}</Typography>
                      </Dropzone.Cover>
                    </Dropzone>
                  )}
                </>
              )}
              <ShowInAppFormGroup />
            </Box>
          )}
        </Edit>
      </form>
    </FormProvider>
  );
};

export default NewEdit;
